/**
 * API Endpoints
 *
 * Definizione centralizzata di tutti gli endpoint API.
 * Facilita la manutenzione e previene errori di digitazione.
 */

import { env } from '@/config/env';

/**
 * URL base per le API
 */
const BASE_URL = env.API_BASE_URL;

/**
 * Pattern per caratteri validi nei path parameters
 * Permette: alfanumerici, dash, underscore
 */
const VALID_PATH_PARAM_PATTERN = /^[a-zA-Z0-9_-]+$/;

/**
 * Sanitizza un parametro path per prevenire path traversal e injection
 * @throws Error se il parametro contiene caratteri non validi
 */
function sanitizePathParam(param: string, paramName = 'id'): string {
  if (!param || param.trim() === '') {
    throw new Error(`Invalid ${paramName}: cannot be empty`);
  }

  // Previeni path traversal
  if (param.includes('..') || param.includes('/') || param.includes('\\')) {
    throw new Error(`Invalid ${paramName}: path traversal not allowed`);
  }

  // Verifica caratteri validi
  if (!VALID_PATH_PARAM_PATTERN.test(param)) {
    throw new Error(
      `Invalid ${paramName}: only alphanumeric characters, dashes, and underscores are allowed`
    );
  }

  return encodeURIComponent(param);
}

/**
 * Costruisce un URL completo per l'endpoint
 */
function buildUrl(path: string): string {
  // Valida che il path inizi con /
  if (!path.startsWith('/')) {
    throw new Error(`Invalid path: ${path}. Path must start with /`);
  }

  // Previeni path traversal nel path stesso
  if (path.includes('..')) {
    throw new Error(`Invalid path: ${path}. Path traversal not allowed`);
  }

  return `${BASE_URL}${path}`;
}

/**
 * Costruisce un URL con parametri query
 */
function buildUrlWithParams(
  path: string,
  params?: Record<string, string | number | boolean | undefined>
): string {
  const url = buildUrl(path);

  if (!params) return url;

  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  });

  const queryString = searchParams.toString();
  return queryString ? `${url}?${queryString}` : url;
}

/**
 * Endpoint per i viaggi
 */
export const tripsEndpoints = {
  /** Lista viaggi */
  list: (params?: Record<string, string | number | boolean | undefined>) =>
    buildUrlWithParams('/trips', params),

  /** Dettaglio viaggio per ID */
  detail: (id: string) => buildUrl(`/trips/${sanitizePathParam(id)}`),

  /** Dettaglio viaggio per codice */
  byCode: (tripCode: string) => buildUrl(`/trips/code/${sanitizePathParam(tripCode, 'tripCode')}`),

  /** Lista tipi viaggio */
  types: () => buildUrl('/trips/types'),

  /** Lista destinazioni */
  destinations: () => buildUrl('/trips/destinations'),

  // --- Passenger Surveys ---

  /** Lista questionari passeggeri di un viaggio */
  surveys: (tripCode: string, params?: Record<string, string | number | boolean | undefined>) =>
    buildUrlWithParams(`/trips/code/${sanitizePathParam(tripCode, 'tripCode')}/surveys`, params),

  /** Invia reminder singolo */
  sendReminder: (tripCode: string, surveyId: number) =>
    buildUrl(`/trips/code/${sanitizePathParam(tripCode, 'tripCode')}/surveys/${surveyId}/reminders`),

  /** Invia reminder bulk */
  sendBulkReminders: (tripCode: string) =>
    buildUrl(`/trips/code/${sanitizePathParam(tripCode, 'tripCode')}/surveys/reminders/bulk`),

  /** Invio mirato primo questionario per il viaggio (admin JWT) */
  sendFirstSurvey: (tripCode: string) =>
    buildUrl(`/trips/code/${sanitizePathParam(tripCode, 'tripCode')}/send-first-survey`),

  /** Export questionari passeggeri */
  exportSurveys: (tripCode: string, params?: Record<string, string | number | boolean | undefined>) =>
    buildUrlWithParams(`/trips/code/${sanitizePathParam(tripCode, 'tripCode')}/surveys/export`, params),

  /** Debug: passeggeri raw da SQL Server */
  debugPassengers: (tripCode: string) =>
    buildUrl(`/trips/code/${sanitizePathParam(tripCode, 'tripCode')}/debug/passengers`),
} as const;

/**
 * Endpoint per l'import dati da SQL Server (eAgency)
 */
export const importEndpoints = {
  /** Risincronizza i passeggeri di un singolo viaggio (admin JWT) */
  passengers: (tripCode: string) =>
    buildUrlWithParams('/import/passengers', {
      tripCode: sanitizePathParam(tripCode, 'tripCode'),
    }),
} as const;

/**
 * Endpoint per le survey
 */
export const surveysEndpoints = {
  /** Lista survey */
  list: (params?: Record<string, string | number | boolean | undefined>) =>
    buildUrlWithParams('/surveys', params),

  /** Dettaglio survey */
  detail: (id: string) => buildUrl(`/surveys/${sanitizePathParam(id)}`),

  /** Invia survey */
  send: () => buildUrl('/surveys/send'),

  /** Survey per token (accesso cliente) */
  byToken: (token: string) => buildUrl(`/surveys/token/${sanitizePathParam(token, 'token')}`),

  /** Resend survey */
  resend: (id: string) => buildUrl(`/surveys/${sanitizePathParam(id)}/resend`),

  /** Export survey in XLSX */
  export: (params?: Record<string, string | number | boolean | undefined>) =>
    buildUrlWithParams('/surveys/export', params),
} as const;

/**
 * Endpoint per statistiche
 *
 * Nota: Gli endpoint analytics avanzati (overview, trip, destination, tripType)
 * sono costruiti direttamente in analytics.api.ts per maggiore flessibilità
 * nella gestione dei parametri.
 */
export const statsEndpoints = {
  /** Statistiche dashboard (cumulativo all-time) */
  dashboard: buildUrl('/stats/dashboard'),
} as const;

/**
 * Endpoint per questionari pubblici (no auth)
 */
export const publicSurveysEndpoints = {
  /** Recupera dati questionario tramite token */
  getByToken: (token: string) =>
    buildUrl(`/surveys/public/${sanitizePathParam(token, 'token')}`),

  /** Invia risposte questionario */
  submit: (token: string) =>
    buildUrl(`/surveys/public/${sanitizePathParam(token, 'token')}/submit`),
} as const;

/**
 * Endpoint per autenticazione
 */
export const authEndpoints = {
  /** Login */
  login: () => buildUrl('/auth/login'),

  /** Logout */
  logout: () => buildUrl('/auth/logout'),

  /** Utente corrente */
  me: () => buildUrl('/auth/me'),

  /** Refresh token */
  refresh: () => buildUrl('/auth/refresh'),

  /** Forgot password - richiesta reset */
  forgotPassword: () => buildUrl('/auth/forgot-password'),

  /** Verifica token di reset password */
  verifyResetToken: () => buildUrl('/auth/verify-reset-token'),

  /** Reset password con token */
  resetPassword: () => buildUrl('/auth/reset-password'),
} as const;

/**
 * Export aggregato di tutti gli endpoint
 */
export const endpoints = {
  trips: tripsEndpoints,
  surveys: surveysEndpoints,
  stats: statsEndpoints,
  publicSurveys: publicSurveysEndpoints,
  auth: authEndpoints,
} as const;
